<template>
  <div class="flex flex-col flex-1 min-h-0">
    <div ref="scrollContainer" class="flex-1 min-h-0 overflow-y-auto p-16">
      <div v-if="loading" class="flex items-center justify-center py-32">
        <Spinner />
      </div>
      <div v-else-if="messages.length === 0 && !hideEmptyState">
        <slot name="emptyState">
          <EmptyStateCard
            :title="emptyTitle"
            :message="emptyMessage"
            :action-text="emptyActionText"
            :action-url="emptyActionUrl"
            :action-to="emptyActionTo"
            @action="$emit('emptyAction')"
          />
        </slot>
      </div>
      <div v-else>
        <div
          v-for="(item, index) in listItemsWithDividers"
          :key="item.type === 'divider' ? `divider-${item.dateLabel}-${index}` : item.message.id"
        >
          <MessageListDayDivider v-if="item.type === 'divider'" :date-label="item.dateLabel" />
          <MessageBubble
            v-else
            :account-type="accountType"
            :body="item.message.body"
            :admin-message-data="item.message.adminMessageData"
            :sender-name="item.message.senderName"
            :sender-avatar-url="item.message.senderAvatarUrl"
            :is-outgoing="item.message.isOutgoing"
            :is-admin="item.message.isAdmin"
            :timestamp="item.message.timestamp"
            :read="item.message.read"
            :message-status="item.message.messageStatus || 'sent'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import type { RawLocation } from 'vue-router';
import debounce from 'lodash/debounce';
import Spinner from '@/components/spinner/Spinner.vue';
import EmptyStateCard from '@/components/empty_state_card/EmptyStateCard.vue';
import MessageBubble from '../messageBubble/MessageBubble.vue';
import MessageListDayDivider from './MessageListDayDivider.vue';
import { getDaySeparatorLabel } from '@/apps/messages/helpers/channelPreview';

interface MessageItem {
  id: string | number;
  body: string;
  adminMessageData?: import('@/apps/messages/helpers/adminMessageFormatter').AdminMessageData | null;
  senderName: string;
  senderAvatarUrl?: string;
  isOutgoing?: boolean;
  isAdmin?: boolean;
  timestamp?: string;
  createdAt?: number;
  read?: boolean;
}

type ListItemWithDivider = { type: 'divider'; dateLabel: string } | { type: 'message'; message: MessageItem };

export default {
  name: 'MessageList',
  components: {
    Spinner,
    EmptyStateCard,
    MessageBubble,
    MessageListDayDivider,
  },
  props: {
    messages: {
      type: Array as PropType<MessageItem[]>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyTitle: {
      type: String,
      default: 'No messages yet',
    },
    emptyMessage: {
      type: String,
      default: 'Start the conversation when you are ready.',
    },
    emptyActionText: {
      type: String,
      default: '',
    },
    emptyActionUrl: {
      type: String,
      default: '',
    },
    emptyActionTo: {
      type: [String, Object] as PropType<RawLocation>,
      default: null,
    },
    hideEmptyState: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Track whether user has scrolled up away from the latest messages
      isUserScrolledUp: false,
      // Distance from bottom (in pixels) to consider the user "at the bottom"
      scrollToBottomThreshold: 100,
      // Debounced scroll handler to avoid excessive function calls
      handleScrollDebounced: null as ReturnType<typeof debounce> | null,
    };
  },
  computed: {
    accountType(): 'parent' | 'sitter' {
      return this.$store.getters['account/accountType'];
    },
    listItemsWithDividers(): ListItemWithDivider[] {
      const items: ListItemWithDivider[] = [];
      let lastDayLabel: string | null = null;

      (this.messages as MessageItem[]).forEach((message) => {
        if (message.createdAt != null) {
          const dayLabel = getDaySeparatorLabel(message.createdAt);
          if (dayLabel !== lastDayLabel) {
            items.push({ type: 'divider', dateLabel: dayLabel });
            lastDayLabel = dayLabel;
          }
        }
        items.push({ type: 'message', message });
      });

      return items;
    },
  },
  watch: {
    messages() {
      // Only auto-scroll if user hasn't manually scrolled up
      if (!this.isUserScrolledUp) {
        this.scrollToBottom();
      }
    },
  },
  mounted() {
    // Initial scroll to show latest messages
    if (this.messages.length > 0) {
      this.scrollToBottom();
    }
    this.setupScrollListener();
  },
  beforeUnmount() {
    // Clean up scroll listener and debounced function to prevent memory leaks
    this.cleanupScrollListener();
  },
  methods: {
    /**
     * Setup scroll event listener with debouncing to track user scroll position.
     * Allows us to detect when user manually scrolls up to read older messages.
     * Otherwise they will be forced to the bottom no matter what
     */
    setupScrollListener() {
      const scrollContainer = this.$refs.scrollContainer as HTMLElement | undefined;
      if (!scrollContainer) return;

      this.handleScrollDebounced = debounce(this.handleScroll.bind(this), 100);
      scrollContainer.addEventListener('scroll', this.handleScrollDebounced);
    },

    /**
     * Clean up scroll event listener and cancel any pending debounced calls.
     * Prevents memory leaks when component is destroyed.
     */
    cleanupScrollListener() {
      const scrollContainer = this.$refs.scrollContainer as HTMLElement | undefined;
      if (!scrollContainer || !this.handleScrollDebounced) return;

      scrollContainer.removeEventListener('scroll', this.handleScrollDebounced);

      // Cancel pending debounced calls
      if (this.handleScrollDebounced.cancel) {
        this.handleScrollDebounced.cancel();
      }

      this.handleScrollDebounced = null;
    },

    /**
     * Handle scroll events to track user intent.
     * Determines if user is still near the bottom (should auto-scroll)
     * or has scrolled up to read history (should not interrupt with auto-scroll).
     */
    handleScroll() {
      const el = this.$refs.scrollContainer as HTMLElement | undefined;
      if (!el) return;

      // Calculate distance from bottom of scrollable container
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

      // If user scrolled more than threshold away from bottom, flag as "scrolled up"
      if (distanceFromBottom > this.scrollToBottomThreshold) {
        this.isUserScrolledUp = true;
      } else {
        // If user scrolled back near bottom, re-enable auto-scroll
        this.isUserScrolledUp = false;
      }
    },

    /**
     * Scroll to the bottom of the message list.
     * Only called when user is already near bottom or on initial load.
     */
    scrollToBottom() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const el = this.$refs.scrollContainer as HTMLElement | undefined;
          if (el) {
            el.scrollTop = el.scrollHeight;
          }
        });
      });
    },
  },
};
</script>
